import type { SolutionPageContent } from '../../types/solution'

export const controlExpensesSolution: SolutionPageContent = {
  slug: 'control-expenses',
  hero: {
    headline: 'See exactly where your money goes',
    subheadline: 'Record every cost in seconds, keep receipts safe, and spot spending that eats into your profit.',
    card: {
      type: 'progress',
      title: 'Expenses · September',
      badge: { label: 'RWF 1.84M', tone: 'default' },
      rows: [
        { label: 'Rent', value: 'RWF 600,000', pct: 33 },
        { label: 'Salaries', value: 'RWF 720,000', pct: 39 },
        { label: 'Transport', value: 'RWF 284,000', pct: 15, tone: 'warning' },
        { label: 'Electricity & water', value: 'RWF 236,000', pct: 13 },
      ],
    },
  },
  challenges: [
    {
      quote: 'Small costs disappear from the cash drawer every day.',
      detail: 'Transport, airtime, a quick repair — none of it gets written down.',
    },
    {
      quote: 'I lose receipts, then can’t prove what I spent.',
      detail: 'At tax time I’m digging through bags of crumpled paper.',
    },
    {
      quote: 'I pay bills late because I forget when they’re due.',
      detail: 'Late fees and cut-offs cost more than the bill itself.',
    },
  ],
  helps: [
    {
      solves: 'Small costs disappear',
      title: 'Record any expense in seconds',
      copy: 'Log a cost from your phone the moment you pay it, choose a category, and note how it was paid — cash, mobile money, or bank.',
      product: 'expenses',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Today’s expenses',
          rows: [
            { label: 'Moto delivery', sublabel: 'Transport · Cash', value: 'RWF 2,000' },
            { label: 'Airtime', sublabel: 'Utilities · Mobile money', value: 'RWF 5,000' },
            { label: 'Door lock repair', sublabel: 'Maintenance · Cash', value: 'RWF 15,000' },
          ],
          footer: { label: 'Total today', value: 'RWF 22,000' },
          action: 'Add expense',
        },
      },
    },
    {
      solves: 'I lose receipts',
      title: 'Receipt photos, saved for good',
      copy: 'Snap a photo of every receipt. It’s attached to the expense and stored safely, even when you’re offline.',
      product: 'expenses',
      visual: {
        kind: 'card',
        card: {
          type: 'ring',
          title: 'Receipts this month',
          pct: 94,
          pctLabel: '94%',
          checks: ['112 expenses recorded', '105 with receipt photos', 'Ready to export for your accountant'],
        },
      },
    },
    {
      solves: 'I pay bills late',
      title: 'Recurring bills and reminders',
      copy: 'Set up rent, salaries, and utilities once. Kayko reminds you before they’re due and records them when paid.',
      product: 'expenses',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Upcoming bills',
          rows: [
            { label: 'Shop rent', sublabel: 'Due 1 Oct', value: 'RWF 600,000', tone: 'warning' },
            { label: 'Electricity (prepaid)', sublabel: 'Due 3 Oct', value: 'RWF 60,000' },
            { label: 'Staff salaries', sublabel: 'Due 30 Sep', value: 'RWF 720,000', tone: 'warning' },
          ],
        },
      },
    },
  ],
  journey: {
    heading: 'How it works',
    subheading: 'From a paid bill to a clear profit report.',
    items: [
      { title: 'Pay a cost', copy: 'In cash, by mobile money, or bank transfer.' },
      { title: 'Record it in seconds', copy: 'Amount, category, and payment method.' },
      { title: 'Snap the receipt', copy: 'Attached and stored safely.' },
      { title: 'See the full picture', copy: 'Expenses flow into your profit and cash flow reports.' },
    ],
  },
  results: [
    { value: '100%', label: 'of expenses recorded with a category' },
    { value: '−15%', label: 'lower monthly spending after review' },
    { value: '0', label: 'late fees on recurring bills' },
  ],
  testimonial: {
    quote: 'I used to lose track of small costs. Now every franc spent is recorded, and I know my real profit.',
    name: 'Job Gashangaza',
    role: 'Owner',
    business: 'Job’s Clothing Shop',
    location: 'Nyarugenge, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/1f9a33f4-6198-4f9d-89dd-0aca07eb18bb.jpg',
    metric: { value: '−15%', label: 'lower monthly spending' },
  },
  products: ['expenses', 'reports', 'business-finance'],
  plan: { name: 'Starter', audience: 'businesses tracking costs closely' },
  faq: [
    {
      question: 'Can I record expenses paid in cash?',
      answer: 'Yes. Record cash, mobile money, card, or bank payments, and Kayko keeps your cash-up accurate.',
    },
    {
      question: 'Can I attach receipts?',
      answer: 'Yes. Take a photo with your phone. Receipts are saved even offline and upload when you reconnect.',
    },
    {
      question: 'Can I set up recurring bills?',
      answer: 'Yes. Rent, salaries, and utilities can repeat automatically with reminders before they’re due.',
    },
    {
      question: 'Can I share expenses with my accountant?',
      answer: 'Yes. Export expenses and receipts to a spreadsheet or PDF at any time.',
    },
    {
      question: 'Do expense records help with Business Financing?',
      answer: 'Complete expense records give a clearer picture of your business, which is part of the eligibility check.',
    },
  ],
  related: ['understand-performance', 'access-business-finance', 'food-beverage'],
  cta: {
    headline: 'Know where every franc goes',
    subheadline: 'Start recording expenses today. Local support in Kigali when you need it.',
  },
}
